/**
@format
@flow
*/
import React from 'react';
import styled from 'styled-components';
import { Heading, SubHeading } from '@ziynx/components/typography';
import { Button } from '@ziynx/components/buttons';
import Colors from '@ziynx/theme/colors';
import { getDimensions } from '@ziynx/components/utilities';
import Link from '@ziynx/components/links';
import type { InlineLink as TextLinkType } from '@ziynx/flow-types/links';
import type { ButtonSquircleType } from '@ziynx/components/buttons';

type TitleType = {
  text?: string,
  color?: string,
  onClick?: (event?: SyntheticEvent<HTMLAnchorElement>) => any | Promise<any>,
  href?: string,
  target?: string,
}

export type Props = $ReadOnly<{|
    width?: number | string,
    height?: number | string,
    enableHover?: boolean,
    title?: TitleType,
    subtitle?: TitleType,
    backgroundColor?: string,
    textLink?: TextLinkType,
    squircleButton?: ButtonSquircleType,
    children?: any,
|}>;

const Container = styled.div`
    display: flex;
    align-items: center;
    width: ${({ width }) => getDimensions(width)};
    height: ${({ height }) => getDimensions(height)};
    background: ${({ backgroundColor }) => backgroundColor};
`;

const TextContainer = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
`;

const Title = styled(Heading)`
    color: ${({ textColor }) => textColor};
    margin-right: 24px;
    ${({ enableHover }) => enableHover && `
      a {
        transition: ease-in-out 200ms;
      };
      a:hover {
        transition: ease-in-out 200ms;
        color: ${Colors.PrimaryBlue};
      };
    `}
`;

const StyledTextLink = styled(Link)`
    white-space: nowrap;
`;

const StyledSubtitle = styled(SubHeading)`
  margin-top: 2px;
  margin-bottom: 16px;
`;

const HeadlineDefault = (props: Props): React$Node => {
  const {
    title,
    subtitle,
    width,
    height,
    textLink,
    backgroundColor,
    squircleButton,
    children,
    enableHover,
    ...rest
  } = props;

  return (
    <>
      <Container
        width={width}
        height={height}
        backgroundColor={backgroundColor}
        {...rest}
      >
        <TextContainer>
          {!title?.onClick && !title?.href
        && (
          <Title textColor={title?.color || Colors.GrayDark}>
            {title?.text}
            {children}
          </Title>
        )}
          {(!!title?.onClick || !!title?.href)
        && (
          <Title enableHover={enableHover}>
            <Link
              onClick={title?.onClick}
              href={title?.href}
              color={title?.color || Colors.GrayVeryDark}
              target={title?.target}
            >
              {title?.text}
              {children}
            </Link>
          </Title>
        )}
          {!!Object.keys(textLink || {}).length && <StyledTextLink {...textLink} large variant="text" />}
          {squircleButton && !textLink
        && (
          <Button
            {...squircleButton}
            onClick={squircleButton?.onClick}
            size={squircleButton?.size || 'extraSmall'}
            icon={{
              name: squircleButton?.icon?.name || 'plus',
              size: squircleButton?.icon?.size || 'mini',
              color: squircleButton?.icon?.color || Colors.PrimaryBlue,
            }}
            variant="squircle"
          />
        )}
        </TextContainer>
      </Container>
      {subtitle && <StyledSubtitle medium color={Colors.GrayDark}>{subtitle.text}</StyledSubtitle>}
    </>
  );
};

HeadlineDefault.defaultProps = {
  width: '100%',
  height: '100%',
  backgroundColor: Colors.White,
};

export default HeadlineDefault;
